import React from "react";
import { useSelector } from "react-redux";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { Typography } from "@mui/material";

import AppState from "../../store/AppState";
import { selectDisplayGradientOnGrid } from "../../store/heatmap/HeatMapSelectors";
import {
  selectCategoryByProductId,
  selectCategoriesByProductId,
  selectFetchingProductIds,
} from "../../store/product/ProductSelectors";
import {
  selectIsOverlaySelectedByType,
  selectOverlay,
} from "../../store/overlay/OverlaySelectors";
import { selectConfigValue } from "../../store/app-config/AppConfigSelectors";
import { selectCurrentLocale } from "../../store/store-list/StoreListSelectors";
import { getProductCategoryNameSFCC } from "../../utils/ProductUtil";
import { OVERLAY_TABS } from "../../utils/Constants";
import Tooltip from "../common/ToolTip";
import { classicTheme } from "../../styles/themes/classic";

interface Props {
  productId: string;
  showOverlay?: boolean;
  isSubstituteOverlay?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    overlay: (props: {
      isSubstituteOverlay?: boolean;
      displayGradientOnGrid?: boolean;
    }) => ({
      position: "absolute",
      backgroundColor: props.displayGradientOnGrid
        ? "rgba(0, 0, 0, 0.45)"
        : "rgba(0, 0, 0, 0.65)",
      top: 0,
      bottom: 0,
      left: 0,
      right: props.displayGradientOnGrid ? 5 : 0,
      overflowY: "auto",
      margin: props.isSubstituteOverlay ? "0" : "5px",
      zIndex: props.isSubstituteOverlay ? 0 : 10,
      pointerEvents: "none",
    }),
    body2: {
      fontWeight: 400,
      fontSize: "16px",
    },
    list: {
      fontWeight: 700,
      fontSize: "12px",
      lineHeight: "20px",
    },
    listParent: {
      position: "relative",
      marginTop: 50,
      margin: 0,
      fontWeight: "bold",
      pointerEvents: "auto",
    },
    listItem: {
      color: theme.palette.common.white,
      padding: "0 10px",
      fontSize: "0.8rem",
      "&:hover": {
        color: classicTheme.palette.primary.main,
      },
    },
  }),
);

const textStyle = {
  fontSize: "0.8rem",
};

const ProductCategoryOverlay: React.FC<Props> = (props) => {
  const { productId, showOverlay = false, isSubstituteOverlay = false } = props;
  const displayGradientOnGrid = useSelector(selectDisplayGradientOnGrid);
  const classes = useStyles({ isSubstituteOverlay, displayGradientOnGrid });

  const currentLocaleId = useSelector(selectCurrentLocale) ?? "";
  const defaultLocaleId = useSelector((state: AppState) =>
    selectConfigValue(state, "defaultLocaleId"),
  );

  const categoryList = useSelector((state: AppState) =>
    selectCategoryByProductId(state, productId),
  );
  const categoriesByProductId = useSelector(selectCategoriesByProductId);
  const fetchingProductIds = useSelector(selectFetchingProductIds);
  const isCategoryOverlay = useSelector((state: AppState) =>
    selectIsOverlaySelectedByType(state, OVERLAY_TABS.CATEGORIES_OVERLAY),
  );
  const selectedOverlay = useSelector(selectOverlay);

  // Check if this specific product has been fetched (exists in category state)
  const isThisProductFetched = categoriesByProductId.hasOwnProperty(productId);
  // Check if this specific product is currently being fetched
  const isThisProductFetching = fetchingProductIds.includes(productId);

  const shouldShow =
    showOverlay ||
    (isCategoryOverlay && selectedOverlay === OVERLAY_TABS.CATEGORIES_OVERLAY);

  // Check if categories exist for this specific product
  const hasCategoriesForThisProduct = categoryList.length > 0;

  // If categories exist for this product, always show them
  if (hasCategoriesForThisProduct) {
    return (
      <>
        {shouldShow && (
          <div className={classes.overlay}>
            <ul className={classes.listParent}>
              {categoryList.map((obj, index) => (
                <li key={`${productId}_${index}`} className={classes.listItem}>
                  <div>
                    <Tooltip tooltipTitle={obj.code} placement="left">
                      <Typography noWrap variant="inherit" sx={textStyle}>
                        {getProductCategoryNameSFCC(
                          obj,
                          currentLocaleId,
                          defaultLocaleId,
                        )}
                      </Typography>
                    </Tooltip>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  }

  // If product has no categories, show empty overlay (only if this product was already fetched)
  if (isThisProductFetched && !hasCategoriesForThisProduct) {
    return (
      <>
        {shouldShow && (
          <div className={classes.overlay}>
            <ul className={classes.listParent}></ul>
          </div>
        )}
      </>
    );
  }

  // Show loading only if this specific product is being fetched
  if (isThisProductFetching) {
    return (
      <>
        {shouldShow && (
          <div className={classes.overlay}>
            <ul className={classes.listParent}>
              <li key={`${productId}_loading`} className={classes.listItem}>
                <div>
                  <Tooltip tooltipTitle={"load"} placement="left">
                    <Typography
                      noWrap
                      variant="inherit"
                      className={classes.list}
                      sx={textStyle}
                    >
                      Loading. . .
                    </Typography>
                  </Tooltip>
                </div>
              </li>
            </ul>
          </div>
        )}
      </>
    );
  }

  return <></>;
};

export default React.memo(ProductCategoryOverlay);
